import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { IoChevronBack } from 'react-icons/io5'

import { GoogleLoginButton } from '@/components/features/GoogleLoginButton/GoogleLoginButton'
import { Input, PasswordEyeInput } from '@/components/forms'
import { Button, Spinner, Card } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { ROUTES } from '@/constants/routes'
import { sanitizeEmail } from '@/lib/sanitize'
import { logger } from '@/lib/logger'
import type { LoginRequest, AuthResult } from '@/types/auth'

import authStyles from '../Auth.module.css'

export function Login() {
    const navigate = useNavigate()
    const { logIn, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<LoginRequest>({
        email: '',
        password: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(ROUTES.projects)
        }
    }, [isLogged, navigate])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState: LoginRequest) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: LoginRequest) => {
        const { email, password } = formData
        
        if (!email || !password) {
            return { isValid: false, message: 'Por favor completa todos los campos' }
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: 'Por favor ingresa un correo válido' }
        }
        
        return { isValid: true, message: '' }
    }

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        // Sanitize inputs before sending to API
        const sanitizedData: LoginRequest = {
            email: sanitizeEmail(formData.email),
            password: formData.password, // Don't sanitize passwords
        }

        try {
            const status: AuthResult = await logIn(sanitizedData, () => logger.info('2FA required'));

            if (status === 'success') {
                toast.success('Inicio de sesión exitoso')
                navigate(ROUTES.projects)
            }

            if (status === 'confirm_email') {
                toast.success('Por favor confirma tu correo')
                navigate(ROUTES.verifyEmail)
            }

            return
        } catch (error) {
            toast.error('Ocurrió un error al iniciar sesión')
            logger.error('Login error:', error)
        }
    }

    // const handleLoginWithGoogle = async () => {
    //     return
    // }

    return (
        <div className={authStyles.container}>
            <div className={authStyles.header}>
                <button 
                    className={authStyles.backButton}
                    onClick={() => navigate(ROUTES.home)}
                    aria-label="Back to home"
                >
                    <IoChevronBack /> Volver al inicio
                </button>
            </div>
            <Card className={authStyles.card}>
                <h1 className={authStyles.title}>Bienvenido de nuevo</h1>

                <form className={authStyles.form} onSubmit={handleFormSubmit}>
                    <Input
                        name="email"
                        variant="bordered"
                        value={formData.email}
                        setValue={(value) => handleFormDataChange('email', value)}
                        label="Correo electrónico"
                        placeholder="Ingresa tu correo"
                    />
                    <PasswordEyeInput
                        name="password"
                        value={formData.password}
                        setValue={(value) => handleFormDataChange('password', value)}
                        label="Contraseña"
                        placeholder="Ingresa tu contraseña"
                    />

                    <Button
                        variant="primary"
                        size="md"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Iniciar sesión'}
                    </Button>
                </form>

                <div className={authStyles.divider}>
                    <span>o</span>
                </div>
                <GoogleLoginButton />

                <div className={authStyles.footer}>
                    <button
                        className="text-btn"
                        onClick={() => navigate(ROUTES.forgotPassword)}
                    >
                        ¿Olvidaste tu contraseña?
                    </button>
                </div>
            
                <div className={authStyles.footer}>
                    <span>¿No tienes una cuenta?</span>
                    <button
                        className="text-btn"
                        onClick={() => navigate(ROUTES.register)}
                    >
                        <span className={authStyles.link}>Crear cuenta</span>
                    </button>
                </div>
            </Card>
        </div>
    )
}

